import { useState } from "react";
import { styleOptions, colorOptions, seasonOptions, sortOptions } from "@/mocks/categories";

export interface FilterState {
  styles: string[];
  colors: string[];
  seasons: string[];
  sort: string;
}

interface FilterBarProps {
  filters: FilterState;
  resultCount: number;
  onChange: (next: Partial<FilterState>) => void;
  onReset: () => void;
}

function Group({
  title,
  options,
  selected,
  onToggle,
}: {
  title: string;
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-semibold uppercase tracking-wider text-foreground-500">{title}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = selected.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onToggle(opt.value)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors cursor-pointer ${
                active
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function FilterBar({ filters, resultCount, onChange, onReset }: FilterBarProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggle = (key: "styles" | "colors" | "seasons", value: string) => {
    const list = filters[key];
    const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
    onChange({ [key]: next } as Partial<FilterState>);
  };

  const activeCount = filters.styles.length + filters.colors.length + filters.seasons.length;

  const body = (
    <div className="flex flex-col gap-4">
      <Group title="风格" options={styleOptions} selected={filters.styles} onToggle={(v) => toggle("styles", v)} />
      <Group title="颜色" options={colorOptions} selected={filters.colors} onToggle={(v) => toggle("colors", v)} />
      <Group title="季节" options={seasonOptions} selected={filters.seasons} onToggle={(v) => toggle("seasons", v)} />
    </div>
  );

  return (
    <>
      <div className="hidden rounded-lg border border-foreground-200/70 bg-background-50 p-5 lg:block">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex flex-col gap-4">{body}</div>
          <div className="flex flex-col items-end gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-foreground-500">排序</span>
              <div className="flex rounded-full bg-background-100 p-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onChange({ sort: opt.value })}
                    className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors cursor-pointer ${
                      filters.sort === opt.value
                        ? "bg-foreground-950 text-background-50"
                        : "text-foreground-700 hover:text-foreground-950"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={onReset}
              className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-foreground-500 transition-colors hover:text-primary-600 cursor-pointer"
            >
              <i className="ri-refresh-line" /> 重置筛选
            </button>
          </div>
        </div>
      </div>

      <div className="lg:hidden">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-foreground-200 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 cursor-pointer"
          >
            <i className="ri-filter-3-line" /> 筛选
            {activeCount > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-500 px-1 text-xs text-background-50">
                {activeCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => onChange({ sort: filters.sort === "date" ? "popular" : "date" })}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-foreground-200 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 cursor-pointer"
          >
            <i className="ri-sort-desc" /> {filters.sort === "date" ? "最新发布" : "最热"}
          </button>
        </div>

        {drawerOpen && (
          <div className="fixed inset-0 z-[60]">
            <div
              className="absolute inset-0 bg-foreground-950/50"
              onClick={() => setDrawerOpen(false)}
              aria-hidden="true"
            />
            <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 p-5">
              <div className="mb-5 flex items-center justify-between">
                <h3 className="font-heading text-lg font-bold text-foreground-950">筛选条件</h3>
                <button
                  type="button"
                  aria-label="关闭"
                  onClick={() => setDrawerOpen(false)}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-lg text-foreground-700"
                >
                  <i className="ri-close-line" />
                </button>
              </div>
              {body}
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={onReset}
                  className="h-11 flex-1 whitespace-nowrap rounded-lg border border-foreground-200 text-sm font-medium text-foreground-800 cursor-pointer"
                >
                  重置
                </button>
                <button
                  type="button"
                  onClick={() => setDrawerOpen(false)}
                  className="h-11 flex-1 whitespace-nowrap rounded-lg bg-foreground-950 text-sm font-semibold text-background-50 cursor-pointer"
                >
                  查看 {resultCount} 条结果
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}